/**
 * Three demos behind one picker: the referral desk, the Keeping Children Safe course (the Aprendia slice:
 * one flow per module, repeated from the same shape) and a tools lab with one ai item per tool.
 *
 * The referral desk is a Signpost style smoke playbook. main runs the intake and moves into the flow of the
 * country the contact is in, because what you can tell someone depends on where they are. Exercises the
 * capture and confirm loop, conditions that keep a restart from asking twice for what it already knows,
 * a menu that jumps around inside its own flow, a condition filtering an item out, an option that is only
 * on offer sometimes, and an ai item.
 *
 * There is no call and no return. The callback question is written once per country flow, which is less
 * to read than a stack, and every item with options leaves through one of them. The ai item has none, so
 * it leaves through the exit word.
 */

import { KCS } from "./example-content"

const TRAINER = "You are a master trainer talking to a fellow teacher in a crisis-affected school, over WhatsApp. The teacher is answering the reflection question of a strategy. Acknowledge the answer in one or two sentences, specific to what they said, no exclamation marks, then call the transition for an answered reflection. If they ask for an example, read the example reference and give it in three sentences at most, then ask the reflection again. If they share a real, current concern about a child, call the concern transition at once. Never ask for names or identifying details."
const GRADER = "You grade one quiz answer of a teacher course. Compare the answer with what a good answer contains, judging the idea, not the exact words; informal language is fine. If the answer is blank or off topic, rephrase the question once and call nothing. Otherwise write one sentence of feedback (one strength or the key idea they missed, never the words wrong or incorrect) and call the matching transition. Never reveal scores or keywords."
const FINAL_GRADER = "You grade one answer of a final quiz, judging the idea, not the exact words. Your whole reply is exactly: Got it. Never hint whether it was right. When the answer misses the idea, add the concept it missed, in a few words, to the playbook note kcs_missed (read it first and keep what is there). Then call the matching transition."

// One flow per module of Keeping Children Safe, all the same shape: the flow written once and repeated with
// the data of each module, the way an author or the designer would duplicate it. Every strategy and every
// question is its own item, so the flow reads top to bottom with no counters but the score
function kcsModule(n: number): PlaybookFlow {
  const module = KCS[n]
  const flow = `kcs_${n}`
  const concern: PlaybookTransition = { description: "the teacher shares a real, current concern about a child", action: { type: "goto", flow, item: "concern" } }

  const strategies = [1, 2, 3, 4].map((i): PlaybookItem => {
    const strategy = module.strategy[i]
    return {
      id: `strategy_${i}`,
      title: `Strategy ${i}: ${strategy.title}`,
      type: "ai",
      say: `📍 Strategy ${i} of 4: *${strategy.title}*\n\n${strategy.text}\n\n❓ ${strategy.reflection}`,
      prompt: `${TRAINER}\n\n${KCS.rules}\n\nModule: ${module.title}\nStrategy: ${strategy.title}\n${strategy.text}\nReflection asked: ${strategy.reflection}`,
      references: [{ description: "A worked example of this strategy, for when the teacher asks for one", text: strategy.example }],
      transitions: [
        { description: "the teacher answered the reflection question", action: { type: "next" } },
        concern,
        { description: "the teacher wants to leave the course and go back to the demos menu", action: { type: "reset" } },
      ],
    }
  })

  // The open and multiple choice questions: the model grades, the transition writes the verdict
  const questions = [2, 3].map((i): PlaybookItem => ({
    id: `quiz_${i}`,
    title: `Quiz ${i}`,
    type: "ai",
    say: module.quiz[i].question,
    prompt: `${GRADER}\n\nQuestion: ${module.quiz[i].question}\nA good answer: ${module.quiz[i].expected}`,
    transitions: [
      { description: "the answer shows the idea of a good answer", action: { type: "next" }, inc: { kcs_correct: 1 } },
      { description: "the answer misses the idea of a good answer", action: { type: "next" } },
      concern,
    ],
  }))

  return {
    title: `KCS module ${n}`,
    items: [
      {
        id: "module_intro",
        title: "Module intro",
        say: `📘 *Module ${n}: ${module.title}*\n\n${module.intro}`,
        options: [{ label: "Let's go", match: ["go", "ok", "start"], set: { kcs_module: n } }],
      },
      ...strategies,
      {
        id: "recap",
        title: "Recap",
        say: `${module.recap}\n\nNow three quick questions. You need all three to move on.`,
        options: [{ label: "Start the quiz", match: ["quiz", "ok", "go"], set: { kcs_correct: 0 } }],
      },
      {
        // Graded without the model: the first question of every module is a false statement
        id: "quiz_1",
        title: "Quiz 1: true or false",
        say: module.quiz[1].question,
        classify: true,
        options: [
          { label: "True", match: ["yes", "t"] },
          { label: "False", match: ["no", "f"], inc: { kcs_correct: 1 } },
        ],
      },
      ...questions,
      {
        id: "passed",
        title: "Module passed",
        conditions: [["kcs_correct", ">=", 3]],
        say: `✅ You completed Module ${n}.`,
        options: [
          n < 3
            ? { label: "Next module", match: ["next", "continue"], set: { kcs_module: n + 1 }, action:{ type: "goto", flow: `kcs_${n + 1}`, item: "module_intro" } }
            : { label: "Final quiz", match: ["final", "quiz"], action: { type: "goto", flow: "kcs", item: "final_intro" } },
          { label: "Back to the demos", match: ["back", "menu"], action: { type: "reset" } },
        ],
      },
      {
        // Ungated, so the filtered item above can never fall off the end
        id: "retry",
        title: "Module not passed",
        say: "Not quite there yet. Let's look at the key ideas again and then try the questions once more.",
        options: [{ label: "Review and retry", match: ["retry", "ok"], action: { type: "goto", flow, item: "recap" } }],
      },
      {
        // The safeguarding interruption, reached from any ai item of the module by its own transition. There
        // is no Local Context Brief in the demo, so the fixed fallback line is all there is
        id: "concern",
        title: "Current concern",
        say: KCS.concern,
        options: [
          { label: "Continue the course", match: ["continue", "ok"], action: { type: "goto", flow, item: "module_intro" } },
          { label: "Back to the demos", match: ["back", "menu"], action: { type: "reset" } },
        ],
      },
    ],
  }
}

// The course intro and the final quiz: seven questions, one item each. The model never says how it went,
// "Got it" and the next question. What was missed goes to the playbook notepad, where the review reads it
function kcsCourse(): PlaybookFlow {
  const finals = [1, 2, 3, 4, 5, 6, 7].map((i): PlaybookItem => ({
    id: `final_${i}`,
    title: `Final question ${i}`,
    type: "ai",
    say: `*Question ${i} of 7*\n\n${KCS.final[i].question}`,
    prompt: `${FINAL_GRADER}\n\nQuestion: ${KCS.final[i].question}\nA good answer: ${KCS.final[i].expected}`,
    tools: [{ type: "notes" }],
    transitions: [
      { description: "the answer shows the idea of a good answer", action: { type: "next" }, inc: { kcs_final: 1 } },
      { description: "the answer misses the idea of a good answer", action: { type: "next" } },
    ],
  }))

  return {
    title: "Keeping Children Safe",
    items: [
      {
        id: "intro",
        title: "Course intro",
        say: KCS.intro,
        options: [
          { label: "Start", match: ["begin", "yes"], action: { type: "goto", flow: "kcs_1", item: "module_intro" } },
          { label: "Continue with Module 2", match: ["continue"], conditions: [["kcs_module", "==", 2]], action: { type: "goto", flow: "kcs_2", item: "module_intro" } },
          { label: "Continue with Module 3", match: ["continue"], conditions: [["kcs_module", "==", 3]], action: { type: "goto", flow: "kcs_3", item: "module_intro" } },
          { label: "Back to the demos", match: ["back", "menu"], action: { type: "reset" } },
        ],
      },
      {
        id: "final_intro",
        title: "Final quiz intro",
        say: KCS.final.intro,
        options: [{ label: "Start the final quiz", match: ["start", "ok", "go"], set: { kcs_final: 0 } }],
      },
      ...finals,
      {
        id: "final_passed",
        title: "Course completed",
        conditions: [["kcs_final", ">=", 6]],
        say: KCS.completion,
        options: [
          { label: "Return to the demos", match: ["menu", "back"], action: { type: "reset" } },
          { label: "Try the tools lab", match: ["tools", "lab"], action: { type: "goto", flow: "lab", item: "menu" } },
        ],
      },
      {
        id: "final_review",
        title: "Review and choice",
        type: "ai",
        say: "You worked hard through this. Let's go over a few ideas together. Reply when you are ready.",
        prompt: `Read the playbook note kcs_missed. For each concept in it, explain the idea in one warm sentence, in plain classroom language. Never say wrong or incorrect and never give a score. Then clear the note by writing it empty, and ask whether they want to retake the course from Module 1 or try a new final quiz. When they choose, call the matching transition.\n\n${KCS.rules}`,
        tools: [{ type: "notes" }],
        transitions: [
          { description: "the teacher wants to try a new final quiz", action: { type: "goto", flow: "kcs", item: "final_intro" } },
          { description: "the teacher wants to retake the course from Module 1", action: { type: "goto", flow: "kcs_1", item: "module_intro" } },
        ],
      },
    ],
  }
}

export const examplePlaybook: Playbook = {
  id: "pb_referral",
  title: "Signpost Referral Desk",
  format: 1,

  // on_lost is where a conversation goes when the item it was parked on no longer exists. Here it is the
  // country question rather than the top: it re-enters the country flow, which is where everything is
  config: { model: "anthropic/claude-haiku-4-5-20251001", language: "en", languages: ["es", "fr"], on_lost: { flow: "main", item: "ask_country" } },

  // No escalation and nothing counted: with no catch-all anywhere, anything that is not one of the options
  // puts this in front of the question and asks again
  defaults: {
    on_no_match: { say: "Please pick one of the options." },
  },

  flows: {
    main: {
      items: [
        {
          // The demo picker. Every reset comes back here. classify turns "the safeguarding course" into the
          // second option once nothing matched on its own
          id: "start",
          title: "Pick a demo",
          say: "Which demo do you want to try?",
          classify: true,
          options: [
            { label: "📍 Referral desk", match: ["referral", "desk"] },
            { label: "🛡️ Keeping Children Safe", match: ["kcs", "course"], action: { type: "goto", flow: "kcs", item: "intro" } },
            { label: "🧪 Tools lab", match: ["tools", "lab"], action: { type: "goto", flow: "lab", item: "menu" } },
          ],
        },
        {
          // The three items of the intake carry the same kind of condition: a var that remembers the answer, so
          // a reset walks past them instead of asking a second time. It is also what lets the platform ring seed
          // a known contact and have the desk skip straight to the question it does not have yet
          id: "consent",
          title: "Consent",
          conditions: [["consent", "!=", true]],
          say: "Hello, this is the Signpost desk. May we store your answers to refer you to a service?",
          options: [
            { label: "Yes", match: ["ok", "sure", "i agree"], set: { consent: true } },
            // Nothing here writes consent, so the reset comes straight back to this same question. That is what
            // "we cannot continue without it" means when there is nowhere else to be
            { label: "No", match: ["nope"], say: "We cannot continue without it.", action: { type: "reset" } },
          ],
        },
        {
          // An item only captures if it names a var. Free text, so no options: whatever the contact writes
          // lands in vars.name and the conversation moves on
          id: "ask_name",
          title: "Ask name",
          conditions: [["name", "isEmpty"]],
          say: "What is your name?",
          set: "name",
        },
        {
          // The confirmation loop, which is what a capture is normally worth: No forgets the name and sends the
          // cursor back up, which re-opens the item above because its condition reads the var that was just
          // cleared. Going backwards is a jump, so it lives on an option
          id: "confirm_name",
          title: "Confirm name",
          conditions: [["name_confirmed", "isEmpty"]],
          say: "Thanks, {{name}}. Did I get that right?",
          options: [
            { label: "Yes", match: ["correct", "right"], set: { name_confirmed: true } },
            { label: "No", match: ["wrong", "nope"], say: "Let me take that again.", set: { name: null }, action: { type: "goto", flow: "main", item: "ask_name" } },
          ],
        },
        {
          // Last item of main and deliberately ungated: someone who starts over is usually somewhere else, or
          // asking on behalf of someone who is. Its options are what picks the desk that answers
          id: "ask_country",
          title: "Ask country",
          say: "Where are you right now?",
          options: [
            { label: "🇬🇷 Greece", match: ["grecia"], set: { country: "Greece" }, action: { type: "goto", flow: "greece", item: "menu" } },
            { label: "🇮🇹 Italy", match: ["italia"], set: { country: "Italy" }, action: { type: "goto", flow: "italy", item: "menu" } },
          ],
        },
      ],
    },

    greece: {
      items: [
        {
          id: "menu",
          title: "Greece menu",
          intent: "The menu lives inside the country flow and jumps around it. Nothing returns to main on its own",
          say: "What do you need help with in {{country}}, {{name}}?",
          // The only item that declares its ids: these are what a referral report counts, so they cannot move when the copy is reworded or translated. Everywhere else the label is identity enough
          options: [
            { id: "legal", label: "⚖️ Legal aid", match: ["1", "lawyer", "papers"], action: { type: "goto", flow: "greece", item: "legal" } },
            { id: "medical", label: "🏥 Medical care", match: ["2", "doctor", "health"], action: { type: "goto", flow: "greece", item: "medical" } },
            { id: "ask", label: "💬 Ask a question", match: ["3", "question"], action: { type: "goto", flow: "greece", item: "qa" } },
            { id: "done", label: "That is all", match: ["4", "nothing"], action: { type: "goto", flow: "greece", item: "wrap" } },
          ],
        },
        {
          id: "legal",
          title: "Legal aid",
          say: "In Greece, GCR offers free legal counselling in Athens and Thessaloniki.",
          options: [{ label: "Back to the menu", match: ["menu", "back"], action: { type: "goto", flow: "greece", item: "menu" } }],
        },
        {
          id: "medical",
          title: "Medical care",
          say: "Primary care is free at public health centres. Bring any document you have.",
          options: [
            // No action: whoever wants more falls through to the items below, which is what a sequence is for
            { label: "Tell me more", match: ["more"] },
            { label: "Back to the menu", match: ["menu", "back"], action: { type: "goto", flow: "greece", item: "menu" } },
          ],
        },
        {
          id: "ask_age",
          title: "Ask age",
          intent: "Captured once, and what comes after it is filtered by the answer",
          conditions: [["age", "isEmpty"]],
          say: "How old are you? Some of what I can offer depends on it.",
          set: "age",
        },
        {
          // The filter, and the whole of what a condition does: it decides whether this item runs, never
          // where the conversation goes. A minor simply does not see it and moves on to the one below
          id: "adult_clinic",
          title: "Adult clinic",
          conditions: [["age", ">=", 18]],
          say: "There is also a walk-in clinic for adults on Alexandras Avenue, no appointment needed.",
          options: [{ label: "Back to the menu", match: ["menu", "back"], action: { type: "goto", flow: "greece", item: "menu" } }],
        },
        {
          // The last item of a filtered run carries no condition, so the run can never fall off the end of
          // the flow. That is what the validator checks, and it is why this one is not guarded by age < 18
          id: "minor_support",
          title: "Minor support",
          say: "For under 18s, METAdrasi runs a guardianship and health support programme.",
          options: [{ label: "Back to the menu", match: ["menu", "back"], action: { type: "goto", flow: "greece", item: "menu" } }],
        },
        {
          // Written out here and again in italy, which is the trade the removal of call bought: two short
          // questions instead of a shared flow, a stack, and a rule about where a return lands
          id: "ask_phone",
          title: "Ask phone",
          say: "What number should we call?",
          set: "phone",
        },
        {
          // No action on its option, so it falls through to the item below
          id: "confirm_phone",
          title: "Confirm phone",
          say: "Noted, we will call you at {{phone}}.",
          options: [{ label: "Thanks", match: ["thank you", "ok"] }],
        },
        {
          id: "wrap",
          title: "Wrap up",
          say: "Anything else before you go?",
          options: [
            // Only on offer while we do not have the number, so it disappears on the way back from
            // confirm_phone. An option with a condition that does not hold is not drawn, and typing its
            // label does not select it either
            { label: "Ask for a callback", match: ["call me"], conditions: [["phone", "isEmpty"]], action: { type: "goto", flow: "greece", item: "ask_phone" } },
            { label: "Back to the menu", match: ["menu", "back"], action: { type: "goto", flow: "greece", item: "menu" } },
            { label: "No, that is all", match: ["nothing else"], say: "Take care, {{name}}.", action: { type: "reset" } },
          ],
        },
        {
          // Last item of greece on purpose: an ai item leaves through no options and nothing follows it. It
          // declares one transition, back to the menu; the runtime fires it today through the literal exit word,
          // until a real tool-calling loop lets the model call it itself
          id: "qa",
          title: "Ask a question",
          type: "ai",
          intent: "Open questions. No options, so no quick replies: the exit word is the only way out until a tool can do it",
          // say is emitted once, on entry, and interpolates. prompt is never emitted, it goes to the adapter
          // on every message that lands here, and stays plain text so it can be cached. What the model needs
          // to know about this contact goes in context, which is the one that renders vars
          // Both say and prompt mention the exit because nothing is drawn: with no options there is no button to see
          say: "Go ahead, ask me anything about services in {{country}}. Type exit to go back to the menu.",
          prompt: "You are the Signpost desk. Answer briefly and only about available services in the contact's country. Say so when you do not know. Close every answer by telling the contact to type exit to go back to the menu.",
          context: "The contact's name is {{name}} and they are in {{country}}.",
          transitions: [
            { description: "the contact wants to leave the question and go back to the main menu", action: { type: "reset" } },
          ],
        },
      ],
    },

    // Same services, different country, different answers. That is the whole reason the flow is the country
    italy: {
      items: [
        {
          id: "menu",
          title: "Italy menu",
          say: "What do you need help with in {{country}}, {{name}}?",
          options: [
            { id: "legal", label: "⚖️ Legal aid", match: ["1", "lawyer", "papers"], action: { type: "goto", flow: "italy", item: "legal" } },
            { id: "done", label: "That is all", match: ["nothing"], action: { type: "goto", flow: "italy", item: "wrap" } },
          ],
        },
        {
          id: "legal",
          title: "Legal aid",
          say: "In Italy, ASGI runs a free legal helpline for asylum seekers.",
          options: [{ label: "Back to the menu", match: ["menu", "back"], action: { type: "goto", flow: "italy", item: "menu" } }],
        },
        {
          id: "ask_phone",
          title: "Ask phone",
          say: "What number should we call?",
          set: "phone",
        },
        {
          id: "confirm_phone",
          title: "Confirm phone",
          say: "Noted, we will call you at {{phone}}.",
          options: [{ label: "Thanks", match: ["thank you", "ok"] }],
        },
        {
          id: "wrap",
          title: "Wrap up",
          say: "Anything else before you go?",
          options: [
            { label: "Ask for a callback", match: ["call me"], conditions: [["phone", "isEmpty"]], action: { type: "goto", flow: "italy", item: "ask_phone" } },
            { label: "No, that is all", match: ["nothing else"], say: "Take care, {{name}}.", action: { type: "reset" } },
          ],
        },
      ],
    },

    // Keeping Children Safe, the vertical slice of Aprendia: the course flow and one flow per module
    kcs: kcsCourse(),
    kcs_1: kcsModule(1),
    kcs_2: kcsModule(2),
    kcs_3: kcsModule(3),

    // One item per tool of the ai item, each with a way back to this menu
    lab: {
      items: [
        {
          id: "menu",
          title: "Tools menu",
          say: "🧪 Each option tries one tool of the ai item.",
          classify: true,
          options: [
            { label: "🧮 Calculator and captures", match: ["calculator", "math"], action: { type: "goto", flow: "lab", item: "class" } },
            { label: "📝 Notepad", match: ["notes"], action: { type: "goto", flow: "lab", item: "notes" } },
            { label: "🔎 Search", action: { type: "goto", flow: "lab", item: "search" } },
            { label: "⚡ Energizer", match: ["generator"], action: { type: "goto", flow: "lab", item: "energizer_need" } },
            { label: "📄 Lesson plan PDF", match: ["pdf", "lesson"], action: { type: "goto", flow: "lab", item: "lesson" } },
            { label: "↩️ Back to the demos", match: ["back"], action: { type: "reset" } },
          ],
        },
        {
          // Captures write typed vars, and the item below branches on one of them
          id: "class",
          title: "Share the books",
          type: "ai",
          say: "Tell me about your class: how many students, how many textbooks, and how you would describe your materials.",
          prompt: "You help a teacher share scarce textbooks. Save the class size, the number of books and the materials level with the save tools as soon as the teacher says them. Use the calculator for every number you give, never do arithmetic in your head. Suggest a grouping in two sentences. When all three are saved and the grouping is given, call the transition for done.",
          captures: [
            { var: "class_size", type: "number", description: "How many students the teacher has" },
            { var: "books", type: "number", description: "How many textbooks the class has" },
            { var: "materials", type: "enum", values: ["very_limited", "few", "some", "many"], description: "How the teacher describes the materials available" },
          ],
          tools: [{ type: "calculator" }],
          transitions: [
            { description: "all three values are saved and the grouping was given", action: { type: "next" } },
            { description: "the teacher wants to go back to the tools menu", action: { type: "goto", flow: "lab", item: "menu" } },
          ],
        },
        {
          id: "large_class",
          title: "Large class tip",
          conditions: [["class_size", ">", 60]],
          say: "With more than 60 students, skip pair work: choral answers and slates reach everyone.",
          options: [{ label: "Got it", match: ["ok"] }],
        },
        {
          id: "class_done",
          title: "Class saved",
          say: "Saved: {{class_size}} students, {{books}} books, materials {{materials}}.",
          options: [{ label: "Back to the tools", match: ["back", "menu"], action: { type: "goto", flow: "lab", item: "menu" } }],
        },
        {
          id: "notes",
          title: "Notepad",
          type: "ai",
          say: "Tell me anything about your students. I keep notes and use them later, even from other items. Say menu to leave.",
          prompt: "Keep short notes about the teacher's class with the note tools: what matters for later lessons goes in the playbook scope, what only matters here in the item scope. Answer in one or two sentences and say what you noted.",
          tools: [{ type: "notes" }],
          transitions: [{ description: "the teacher wants to go back to the tools menu", action: { type: "goto", flow: "lab", item: "menu" } }],
        },
        {
          id: "search",
          title: "Search",
          type: "ai",
          say: "Ask me something to look up. The search tool of this item has no sources yet: add one in the editor first.",
          prompt: "Answer only from what the search tool returns, in three sentences at most. If it returns nothing, say you could not find it.",
          tools: [{ type: "search", description: "Searches the team's documents", sources: [] }],
          transitions: [{ description: "the teacher wants to go back to the tools menu", action: { type: "goto", flow: "lab", item: "menu" } }],
        },
        {
          id: "energizer_need",
          title: "Energizer need",
          say: "What does your class need right now?",
          classify: true,
          options: [
            { label: "Movement", match: ["energy", "move"], set: { need_type: "movement" } },
            { label: "Focus", match: ["calm", "attention"], set: { need_type: "focus" } },
            { label: "Transition", match: ["change", "switch"], set: { need_type: "transition" } },
          ],
        },
        {
          // The model writes a brief and the generator writes the energizer, straight into the reply
          id: "energizer",
          title: "Energizer",
          type: "ai",
          say: "Anything I should know, like space or minutes? Or just say go.",
          prompt: "Call the energizer writer once, with a brief that has the need, the class size if known and anything the teacher said. Write nothing else yourself but one short line. If the teacher asks for another one, call the writer again asking for something different.",
          context: "Need: {{need_type}}. Class size: {{class_size}}.",
          tools: [{ type: "generate", description: "Writes a classroom energizer from a brief", prompt: "Write one classroom energizer in 90 words at most: a title, up to 4 numbered steps, and a closing line that starts with Notice. It must work for a large class with no materials. No physical contact, no song lyrics, no Simon says. Plain text for WhatsApp, *bold* for the title." }],
          transitions: [
            { description: "the teacher wants a different kind of energizer", action: { type: "goto", flow: "lab", item: "energizer_need" } },
            { description: "the teacher wants to go back to the tools menu", action: { type: "goto", flow: "lab", item: "menu" } },
          ],
        },
        {
          id: "lesson",
          title: "Lesson plan PDF",
          type: "ai",
          say: "What lesson do you want to plan? Tell me the topic, the grade and how much time you have.",
          prompt: "Ask at most one follow up question. Then write a lesson plan with a title, the objective and five steps (Warm-up, I Do, We Do, You Do, Wrap-up), each with its minutes, and send it with the pdf tool. In the chat write only one line saying the plan is attached.",
          context: "Class size: {{class_size}}. Materials: {{materials}}.",
          tools: [{ type: "pdf", description: "Sends the lesson plan to the teacher as a PDF" }],
          transitions: [{ description: "the teacher wants to go back to the tools menu", action: { type: "goto", flow: "lab", item: "menu" } }],
        },
      ],
    },

  },
}
